import { apiService } from './api';

const ORDER_IMPORT_TEMPLATE_COLUMNS = [
  'Client Email',
  'Project Name',
  'Site URL',
  'Publisher Email',
  'Submission Type',
  'Content URL',
  'Order Date',
  // Price is intentionally NOT in the template — it's auto-derived from the
  // matched site + client (same rules as the single add-to-cart flow).
];

const ORDER_IMPORT_TEMPLATE_SAMPLE = [
  'client@example.com',
  'myPub,LLC',
  'example.com',
  'publisher@example.com',
  'GBS',
  'https://example.com/article',
  '', // Order Date — leave blank to use today's date
];

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildClientSideTemplate(): Blob {
  const header = ORDER_IMPORT_TEMPLATE_COLUMNS.map(escapeCsvCell).join(',');
  const sample = ORDER_IMPORT_TEMPLATE_SAMPLE.map(escapeCsvCell).join(',');
  const csv = `${header}\n${sample}\n`;
  return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
}

export interface OrderImportValidationError {
  field: string;
  value: unknown;
  error: string;
}

export interface OrderImportResolved {
  clientId: string;
  projectId: string;
  guestBlogSiteId: string;
  publisherId: string | null;
  price: number;
  submissionType: 'GBS' | 'LI' | 'BOTH';
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
  orderDate: string;
}

export interface OrderImportPreviewRow {
  rowIndex: number;
  raw: Record<string, string>;
  resolved: OrderImportResolved | null;
  labels: {
    clientEmail?: string;
    clientName?: string;
    projectName?: string;
    siteUrl?: string;
    publisherName?: string;
    publisherEmail?: string;
  };
  isValid: boolean;
  errors: OrderImportValidationError[];
}

export interface OrderImportPreviewResult {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  rows: OrderImportPreviewRow[];
}

export interface OrderImportSaveResult {
  saved: number;
  skipped: number;
  errors: string[];
  details?: { createdIds: string[] };
  message: string;
}

class OrderBulkUploadService {
  async downloadTemplate(): Promise<Blob> {
    // Template is a fixed CSV — generate it client-side so the feature works even if
    // the backend `/bulk/template` route hasn't been deployed yet.
    return buildClientSideTemplate();
  }

  async parse(file: File): Promise<OrderImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiService.upload<{ success: boolean; data: OrderImportPreviewResult }>(
      '/guest-blog-orders/bulk/parse',
      formData
    );
    return response.data;
  }

  async save(
    rows: OrderImportPreviewRow[],
    selectedIndices: number[]
  ): Promise<OrderImportSaveResult> {
    const response = await apiService.post<{ success: boolean; data: OrderImportSaveResult }>(
      '/guest-blog-orders/bulk/save',
      { rows, selectedIndices },
      { timeout: 300000 }
    );
    return response.data;
  }
}

export const orderBulkUploadService = new OrderBulkUploadService();
